import { supabase } from '../client';
import { MaskingTemplate, CandidateAlias } from '@core/types';

// Alphabet for alias generation: Candidate A, B, C … Z, AA, AB …
function indexToAlias(n: number): string {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let result = '';
  let idx = n;
  do {
    result = letters[idx % 26] + result;
    idx = Math.floor(idx / 26) - 1;
  } while (idx >= 0);
  return `Candidate ${result}`;
}

export const maskingService = {
  // ── Templates ───────────────────────────────────────────────
  async listTemplates(orgId: string): Promise<MaskingTemplate[]> {
    const { data, error } = await supabase
      .from('ggj_b2b_masking_templates')
      .select('*')
      .eq('org_id', orgId)
      .order('is_default', { ascending: false });
    if (error) throw error;
    return data ?? [];
  },

  async getTemplate(id: string): Promise<MaskingTemplate | null> {
    const { data } = await supabase
      .from('ggj_b2b_masking_templates')
      .select('*')
      .eq('id', id)
      .single();
    return data;
  },

  async getDefaultTemplate(orgId: string): Promise<MaskingTemplate | null> {
    const { data } = await supabase
      .from('ggj_b2b_masking_templates')
      .select('*')
      .eq('org_id', orgId)
      .eq('is_default', true)
      .single();
    return data;
  },

  async createTemplate(payload: Omit<MaskingTemplate, 'id' | 'created_at'>): Promise<MaskingTemplate> {
    const { data, error } = await supabase
      .from('ggj_b2b_masking_templates')
      .insert(payload)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async updateTemplate(id: string, payload: Partial<MaskingTemplate>): Promise<void> {
    const { error } = await supabase
      .from('ggj_b2b_masking_templates')
      .update(payload)
      .eq('id', id);
    if (error) throw error;
  },

  async setDefault(orgId: string, id: string): Promise<void> {
    // Unset previous default
    await supabase
      .from('ggj_b2b_masking_templates')
      .update({ is_default: false })
      .eq('org_id', orgId)
      .eq('is_default', true);
    // Set new default
    await supabase
      .from('ggj_b2b_masking_templates')
      .update({ is_default: true })
      .eq('id', id);
  },

  async deleteTemplate(id: string): Promise<void> {
    const { error } = await supabase.from('ggj_b2b_masking_templates').delete().eq('id', id);
    if (error) throw error;
  },

  // ── Alias engine ────────────────────────────────────────────
  async getOrCreateAlias(
    orgId: string,
    candidateId: string,
    clientId: string
  ): Promise<string> {
    // Try to find existing alias
    const { data: existing } = await supabase
      .from('ggj_b2b_candidate_aliases')
      .select('alias')
      .eq('org_id', orgId)
      .eq('candidate_id', candidateId)
      .eq('client_id', clientId)
      .single();

    if (existing) return existing.alias;

    // Count how many aliases this org has for this client → determines next letter
    const { count } = await supabase
      .from('ggj_b2b_candidate_aliases')
      .select('*', { count: 'exact', head: true })
      .eq('org_id', orgId)
      .eq('client_id', clientId);

    const alias = indexToAlias(count ?? 0);

    const { data: created, error } = await supabase
      .from('ggj_b2b_candidate_aliases')
      .insert({ org_id: orgId, candidate_id: candidateId, client_id: clientId, alias })
      .select('alias')
      .single();

    if (error) throw error;
    return created.alias;
  },

  async listAliases(orgId: string, candidateId?: string): Promise<CandidateAlias[]> {
    let query = supabase
      .from('ggj_b2b_candidate_aliases')
      .select('*')
      .eq('org_id', orgId);
    if (candidateId) query = query.eq('candidate_id', candidateId);
    const { data, error } = await query;
    if (error) throw error;
    return data ?? [];
  },
};
